import React, { useEffect, useState } from 'react';
import '../../../styles/Auth/DashBoard.css';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { toast } from 'react-toastify';
import { getData } from '../../../webService/webservice.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const DashboardPage = ({ sessionToken }) => {
  const [apiData, setApiData] = useState(null);
  const [filter, setFilter] = useState('daily');
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [formData, setFormData] = useState(
    {
      name: "",
      email: "",
      phoneNumber: "",
      employeeImageObject: {},
    }
  );

  const summary = apiData?.summary || {};
  const monthlySales = apiData?.monthlySales || monthLabels.map(() => 0);
  const monthlyPurchases = apiData?.monthlyPurchases || monthLabels.map(() => 0);

  const barData = {
    labels: monthLabels,
    datasets: [
      {
        label: 'Sales (₹)',
        data: monthlySales,
        backgroundColor: monthLabels.map(
          (_, i) => (i + 1 === selectedMonth ? '#00E096' : '#D9D9D9')
        ),
        borderRadius: 10,
        barThickness: 20,
      },
      {
        label: 'Purchases (₹)',
        data: monthlyPurchases,
        backgroundColor: monthLabels.map(
          (_, i) => (i + 1 === selectedMonth ? '#FF4560' : '#E0E0E0')
        ),
        borderRadius: 10,
        barThickness: 20,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (ctx) => `₹${ctx.raw.toLocaleString()}`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (value) => `₹${value}` },
        grid: { drawBorder: false, drawTicks: false, color: 'rgba(0,0,0,0.1)', borderDash: [4, 4] },
      },
      x: { grid: { display: false } },
    },
  };

  const topProducts = apiData?.topProducts || [];

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const response = await getData('dashboard/dashboard/summary', {}, sessionToken);
        setApiData(response?.data || {});
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = sessionStorage.getItem("Token");
        const userId = sessionStorage.getItem("userId");

        const response = await getData(`users/user/${userId}`, token);

        console.log("User API Response:", response);

        const user =
          response?.data?.data || // common API format
          response?.data ||       // fallback
          {};

        setFormData({
          name: user.name || "",
          email: user.email || "",
          phoneNumber: user.phoneNumber || "",
          employeeImageObject: user.employeeImageObject || {},
        });
      } catch (err) {
        console.error("❌ Failed to fetch user details:", err);
      }
    };

    fetchUser();
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();

    if (hour >= 5 && hour < 12) return "Good Morning";
    if (hour >= 12 && hour < 17) return "Good Afternoon";
    return "Good Evening"; 
  };

  const toTitleCase = (str) => {
    if (!str) return "";
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  return (
    <div className="dashboard-layout">      
      <div className="dashboard-content">
        
        <main className="dashboard-main">
          <div className="dashboard-header-row">
  
            <div className="dashboard-welcome">
              {getGreeting()}, {toTitleCase(formData?.name) || "User"}
            </div>

            <div className="dashboard-header-actions">
              {['daily', 'weekly', 'monthly', 'yearly'].map((t) => (
                <button
                  key={t}
                  className={`filter-btn ${filter === t ? 'active' : ''}`}
                  onClick={() => setFilter(t)}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Summary Cards */}
          <div className="dashboard-cards">
            <Card icon="/assets/totalsales.png" title="Total Sales" value={summary?.[filter]?.totalSales} />
            <Card icon="/assets/netprofit.png" title="Net Profit" value={summary?.[filter]?.netProfit} />
            <Card icon="/assets/grossprofit.png" title="Gross Profit" value={summary?.[filter]?.grossProfit} />
            <Card icon="/assets/salesreturn.png" title="Sales Return" value={summary?.[filter]?.salesReturn} />
          </div>

          <div className="dashboard-cards">
            <Card icon="/assets/purchase.png" title="Purchase" value={summary?.[filter]?.purchases} />
            <Card icon="/assets/purchasedue.png" title="Purchase Due" value={summary?.[filter]?.purchaseDue} />
            <Card icon="/assets/expense.png" title="Expense" value={summary?.[filter]?.expenses} />
            <Card icon="/assets/salarycost.png" title="Salary Cost" value={summary?.[filter]?.salaryCost} />
          </div>

          {/* Chart + Top Products */}
          <div className="dashboard-bottom-row">

            {/* Sales/Purchase Flow Chart */}
            <div className="dashboard-graph">
              <h3>Purchase and Sale Flow</h3>

              <div className="Monthly-Filter">
                <label>Select Month:</label>
                <select
                  className="month-select"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                >
                  {monthLabels.map((m, idx) => (
                    <option key={idx} value={idx + 1}>{m}</option>
                  ))}
                </select>
              </div>

              <Bar data={barData} options={barOptions} />
            </div>

            {/* Top Products */}
            <div className="dashboard-table">
              <h3>Top Products</h3>
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Qty Sold</th>
                  </tr>
                </thead>
                <tbody>
                  {topProducts.map((item, idx) => (
                    <tr key={idx}>
                      <td className="product-cell">
                        <img
                          src={`${process.env.REACT_APP_API_URL}${item.image}`}
                          className="product-icon"
                          alt={item.product}
                        />
                        {item.product}
                      </td>
                      <td>{item.quantitySold}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
};


// Small reusable card component
const Card = ({ icon, title, value }) => (
  <div className="dashboard-card">
    <div className="card-left"><img src={icon} alt={title} /></div>
    <div className="card-right">
      <h3>{title}</h3>
      <p>₹{(value || 0).toLocaleString()}</p>
    </div>
  </div>
);


export default DashboardPage;
